import { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'

import { Tab, Tabs, TabList, TabPanel } from '../../common/Tabs'

import Loader from '../../common/loader/Loader'
import SuccessModal from '../../common/customModal/SuccessModal'
import ConfirmActionModal from './ConfirmActionModal'
import BillingStatusBadge from './BillingStatusBadge'
import EnrollmentActionButtons from './EnrollmentActionButtons'
import EnrollmentDetailsCard from './EnrollmentDetailsCard'
import EnrollmentStudentHeader from './EnrollmentStudentHeader'
import EnrollmentActionModals from './EnrollmentActionModals'
import TransferModals from './TransferModals'
import InactiveMemberNotice from './InactiveMemberNotice'

import { ReactComponent as CloseIcon } from '../../../assets/images/common/icons/modal-X-icon.svg'
import { ReactComponent as RefundIcon } from '../../../assets/images/common/icons/history-icon.svg'
import { ReactComponent as CheckmarkIcon } from '../../../assets/images/common/icons/checkbox-checkmark.svg'
import { ReactComponent as EditIcon } from '../../../assets/images/common/icons/edit-icon.svg'
import { ReactComponent as WarningIcon } from '../../../assets/images/common/icons/warning-icon.svg'
import { ReactComponent as MailIcon } from '../../../assets/images/common/icons/mail-icon.svg'
import { ReactComponent as BillingXCircle } from '../../../assets/images/common/icons/billing-x-circle.svg'
import { ReactComponent as ReceiptIcon } from '../../../assets/images/common/icons/money-check-dollar-circle.svg'

import { requestGetEnrollment } from '../../../actions/enrollmentsBillingActions'
import RefundInvoiceModal from './RefundInvoiceModal'
import useEnrollmentActions from '../hooks/useEnrollmentActions'
import CreateEnrollmentModal from '../../classes/program-dashboard-manager/add-students-modal/CreateEnrollmentModal'
import { deserialize } from '../../../utility/serialization'
import {
  formatInvoiceStatusDisplay,
  formatPaymentMethodDisplay,
  wasPaidOnline,
} from '../../../utility/enrollmentsBilling'
import { displayWithCurrency } from '../../../utility/currency'

import classes from './EnrollmentDetailDrawer.module.scss'


const EnrollmentDetailDrawer = ({ enrollment: listEnrollment, communityId, onClose, onActionComplete }) => {
  const { t } = useTranslation()
  const billingEnabled = useSelector(state => state.user.current_user.communities[0].has_billing_enabled) !== false
  const consumerPlan = useSelector(state => state.user.current_user.communities[0].license_type === 'consumer_plan')
  const [enrollment, setEnrollment] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadEnrollment = useCallback(() => {
    setIsLoading(true)
    return requestGetEnrollment({ communityId, enrollmentId: listEnrollment.id })
      .then(response => {
        const data = deserialize(response.data)
        setEnrollment(data.enrollment)
      })
      .catch(() => {
        toast.error(t('enrollmentsBilling.drawer.failedToLoadEnrollment'), { position: toast.POSITION.TOP_RIGHT })
      })
      .finally(() => setIsLoading(false))
  }, [communityId, listEnrollment.id])

  useEffect(() => {
    loadEnrollment()
  }, [loadEnrollment])

  const {
    actionLoading, openApprove, openDecline, openDrop, openDelete, openTransfer,
    invoiceHandlers, handleMarkPaymentStatus, actionModalProps, transferModalProps, invoiceModalProps,
  } = useEnrollmentActions({ listEnrollment, enrollment, onActionComplete, loadEnrollment, onRemoved: onClose })

  const student = enrollment?.student || listEnrollment.student
  const displayStatus = enrollment?.displayStatus || listEnrollment.displayStatus
  const membershipInactive = consumerPlan && student.membershipActive === false
  // The Billing tab is always available once the enrollment loads; when there's no invoice it shows
  // the "No Billing Available" empty state (attach a package to activate billing).
  const showBillingTab = !!enrollment
  const invoice = enrollment?.invoice
  const isOutstanding = invoice?.status === 'outstanding'

  return (
    <>
      <div className={classes.drawer}>
        <header className={classes.header}>
          <button className={classes.closeButton} onClick={onClose}>
            <CloseIcon className={classes.closeIcon} aria-hidden='true' />
          </button>
        </header>

        {isOutstanding && invoice?.lastReminderSentAt && (
          <OutstandingBanner lastReminderDate={invoice.lastReminderSentAt} />
        )}

        <Tabs className={classes.tabs}>
          <TabList className={classes.tabList}>
            <Tab className={classes.tab}>{t('enrollmentsBilling.drawer.tabEnrollments')}</Tab>
            {showBillingTab && <Tab className={classes.tab}>{t('enrollmentsBilling.drawer.tabBilling')}</Tab>}
          </TabList>

          <EnrollmentStudentHeader
            student={student}
            displayStatus={displayStatus}
            actionLoading={actionLoading}
            onApprove={openApprove}
            onDecline={openDecline}
          />

          <TabPanel>
            {isLoading || !enrollment ? (
              <div className={classes.loaderWrapper}>
                {isLoading ? <Loader>{t('enrollmentsBilling.drawer.loadingDetails')}</Loader> :
                  <span>{t('enrollmentsBilling.drawer.failedToLoadDetails')}</span>}
              </div>
            ) : (
              <EnrollmentTab
                enrollment={enrollment}
                displayStatus={displayStatus}
                membershipInactive={membershipInactive}
                onDrop={openDrop}
                onRemove={openDelete}
                onTransfer={openTransfer}
              />
            )}
          </TabPanel>
          {showBillingTab && (
            <TabPanel>
              <BillingTab
                enrollment={enrollment}
                invoiceHandlers={invoiceHandlers}
                onMarkPaymentStatus={handleMarkPaymentStatus}
                actionLoading={actionLoading}
                billingEnabled={billingEnabled}
                membershipInactive={membershipInactive}
              />
            </TabPanel>
          )}
        </Tabs>
      </div>
      <div
        tabIndex={0}
        role='button'
        onKeyDown={() => null}
        className={classes.drawerOverlay}
        onClick={onClose}
      />

      <EnrollmentActionModals {...actionModalProps} />

      <TransferModals {...transferModalProps} />

      <InvoiceModals {...invoiceModalProps} />
    </>
  )
}

// Invoice lifecycle modals: mark paid/outstanding, refund, create/edit.
const InvoiceModals = ({
  activeModal, closeModal, enrollment, invoice, actionLoading,
  handleMarkPaid, handleRefund, handleEditInvoice, handleCreateInvoice, handleMarkOutstanding,
  showMarkPaidSuccess, setShowMarkPaidSuccess, markPaidEmailSent, showReminderSuccess, setShowReminderSuccess,
}) => {
  const { t } = useTranslation()
  return (
    <>
      <SuccessModal
        isOpen={showMarkPaidSuccess}
        onClose={() => setShowMarkPaidSuccess(false)}
        title={markPaidEmailSent ? t('enrollmentsBilling.drawer.markedAsPaidWithEmail') : t('enrollmentsBilling.drawer.markedAsPaid')}
        buttonMessage={t('enrollmentsBilling.drawer.close')}
      />
      <SuccessModal
        isOpen={showReminderSuccess}
        onClose={() => setShowReminderSuccess(false)}
        title={t('enrollmentsBilling.drawer.reminderEmailSent')}
        buttonMessage={t('enrollmentsBilling.drawer.close')}
      />
      {activeModal === 'invoicePaid' && enrollment && (
        <CreateEnrollmentModal
          isOpen
          mode='markAsPaid'
          onClose={closeModal}
          enrollment={enrollment}
          onSubmit={handleMarkPaid}
          isLoading={actionLoading}
        />
      )}
      {activeModal === 'invoiceRefund' && invoice && (
        <RefundInvoiceModal
          isOpen
          onClose={closeModal}
          onSubmit={handleRefund}
          isLoading={actionLoading}
          invoice={invoice}
          enrollment={enrollment}
        />
      )}
      {activeModal === 'invoiceOutstanding' && invoice && (
        <ConfirmActionModal
          isOpen
          onClose={closeModal}
          onConfirm={handleMarkOutstanding}
          isLoading={actionLoading}
          title={t('enrollmentsBilling.drawer.markAsOutstandingTitle')}
          message={t('enrollmentsBilling.drawer.markAsOutstandingMessage')}
          confirmMessage={t('enrollmentsBilling.drawer.markAsUnpaid')}
        />
      )}
      {activeModal === 'invoiceEdit' && enrollment?.invoice && (
        <CreateEnrollmentModal
          isOpen
          mode='edit'
          onClose={closeModal}
          enrollment={enrollment}
          program={enrollment.course}
          onSubmit={handleEditInvoice}
          isLoading={actionLoading}
        />
      )}
      {/* No invoice yet: the same edit modal attaches a package, which creates the invoice. */}
      {activeModal === 'invoiceEdit' && enrollment && !enrollment.invoice && (
        <CreateEnrollmentModal
          isOpen
          mode='edit'
          onClose={closeModal}
          enrollment={enrollment}
          program={enrollment.course}
          onSubmit={handleCreateInvoice}
          isLoading={actionLoading}
        />
      )}
    </>
  )
}

InvoiceModals.propTypes = {
  activeModal: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  enrollment: PropTypes.object,
  invoice: PropTypes.object,
  actionLoading: PropTypes.bool,
  handleMarkPaid: PropTypes.func.isRequired,
  handleRefund: PropTypes.func.isRequired,
  handleEditInvoice: PropTypes.func.isRequired,
  handleCreateInvoice: PropTypes.func.isRequired,
  handleMarkOutstanding: PropTypes.func.isRequired,
  showMarkPaidSuccess: PropTypes.bool,
  setShowMarkPaidSuccess: PropTypes.func.isRequired,
  markPaidEmailSent: PropTypes.bool,
  showReminderSuccess: PropTypes.bool,
  setShowReminderSuccess: PropTypes.func.isRequired,
}

// --- Outstanding Payment Banner ---

const OutstandingBanner = ({ lastReminderDate }) => {
  const { t } = useTranslation()
  return (
    <div className={classes.outstandingBanner}>
      <WarningIcon className={classes.bannerIcon} />
      <span>{t('enrollmentsBilling.drawer.outstandingPaymentReminder', { date: moment(lastReminderDate).format('M/DD/YYYY') })}</span>
    </div>
  )
}

OutstandingBanner.propTypes = {
  lastReminderDate: PropTypes.string.isRequired,
}

// --- Enrollment Tab ---

const EnrollmentTab = ({ enrollment, displayStatus, membershipInactive, onDrop, onRemove, onTransfer }) => (
  <div className={classes.tabContent}>
    <EnrollmentActionButtons
      displayStatus={displayStatus}
      invoice={enrollment.invoice}
      paidOnline={wasPaidOnline(enrollment)}
      membershipInactive={membershipInactive}
      onDrop={onDrop}
      onRemove={onRemove}
      onTransfer={onTransfer}
    />

    <EnrollmentDetailsCard enrollment={enrollment} displayStatus={displayStatus} />
  </div>
)

EnrollmentTab.propTypes = {
  enrollment: PropTypes.object.isRequired,
  displayStatus: PropTypes.string,
  membershipInactive: PropTypes.bool,
  onDrop: PropTypes.func,
  onRemove: PropTypes.func,
  onTransfer: PropTypes.func,
}

// --- Billing Tab ---

// editLocked: billing is read-only once the enrollment is dropped, or was paid online (Stripe holds
const deriveBillingFlags = (enrollment, billingEnabled) => ({
  editLocked: enrollment.displayStatus === 'dropped' || enrollment.paymentStatus === 'paid_online',
  canInvoice: billingEnabled && enrollment.displayStatus !== 'dropped' && enrollment.paymentStatus !== 'paid_online',
  isPaidStatus: enrollment.paymentStatus === 'paid',
  canMarkStatus: enrollment.paymentStatus !== 'paid_online'
    && !['pending_approval', 'waitlisted', 'dropped', 'completed'].includes(enrollment.displayStatus),
})

const NoInvoiceBilling = ({ enrollment, invoiceHandlers, onMarkPaymentStatus, actionLoading, billingEnabled }) => {
  const { t } = useTranslation()
  const { editLocked, canInvoice, isPaidStatus, canMarkStatus } = deriveBillingFlags(enrollment, billingEnabled)

  const createdActivity = (enrollment.enrollmentActivities || []).find(activity => activity.changeType === 'created')
  const coachMessage = createdActivity?.details?.coachMessage

  return (
    <div className={classes.tabContent}>
      {!editLocked && (
        <div className={classes.actionButtons}>
          {canMarkStatus && (
            <button
              className={classes.actionButton}
              onClick={() => onMarkPaymentStatus(isPaidStatus ? 'outstanding' : 'paid')}
              disabled={actionLoading}
            >
              {isPaidStatus ? (
                t('enrollmentsBilling.drawer.markAsOutstanding')
              ) : (
                <>
                  <span className={classes.markPaidCheckbox}><CheckmarkIcon className={classes.markPaidCheckmark} /></span>
                  {t('enrollmentsBilling.drawer.markAsPaid')}
                </>
              )}
            </button>
          )}
          {canInvoice && (
            <button className={classNames(classes.actionButton, classes.pushRight)} onClick={invoiceHandlers.onEdit} disabled={actionLoading}>
              <EditIcon className={classes.billingActionIcon} /> {t('enrollmentsBilling.drawer.edit')}
            </button>
          )}
        </div>
      )}

      <section className={classes.emptyBillingCard}>
        <span className={classes.noPackageBadge}>
          <BillingXCircle className={classes.noPackageBadgeIcon} /> {t('enrollmentsBilling.drawer.noPricingPackage')}
        </span>

        <div className={classes.emptyBilling}>
          <ReceiptIcon className={classes.emptyBillingIcon} />
          <h6 className={classes.emptyBillingTitle}>{t('enrollmentsBilling.drawer.noBillingAvailable')}</h6>
          <p className={classes.emptyBillingText}>
            {t(billingEnabled ? 'enrollmentsBilling.drawer.noBillingDescription' : 'enrollmentsBilling.drawer.billingDisabledDescription')}
          </p>
          {canInvoice && (
            <button className={classes.addPackageButton} onClick={invoiceHandlers.onEdit} disabled={actionLoading}>
              {t('enrollmentsBilling.drawer.addPricingPackage')}
            </button>
          )}
        </div>

        {coachMessage && (
          <div className={classes.notesSection}>
            <span className={classes.notesTitle}>{t('enrollmentsBilling.drawer.additionalNotes')}</span>
            <p className={classes.notesText}>{coachMessage}</p>
          </div>
        )}
      </section>
    </div>
  )
}

NoInvoiceBilling.propTypes = {
  enrollment: PropTypes.object.isRequired,
  invoiceHandlers: PropTypes.object,
  onMarkPaymentStatus: PropTypes.func,
  actionLoading: PropTypes.bool,
  billingEnabled: PropTypes.bool,
}

const BillingTab = ({ enrollment, invoiceHandlers, onMarkPaymentStatus, actionLoading, billingEnabled, membershipInactive }) => {
  const { t } = useTranslation()
  const { invoice, course } = enrollment
  const { editLocked } = deriveBillingFlags(enrollment, billingEnabled)

  if (!invoice) {
    return (
      <NoInvoiceBilling
        enrollment={enrollment}
        invoiceHandlers={invoiceHandlers}
        onMarkPaymentStatus={onMarkPaymentStatus}
        actionLoading={actionLoading}
        billingEnabled={billingEnabled}
      />
    )
  }

  const invoiceDisplay = formatInvoiceStatusDisplay(invoice, t)
  const lineItems = invoice.lineItems || []
  const currency = invoice.currencyCode ? { code: invoice.currencyCode } : null
  const familyAdminEmail = enrollment.student?.familyAdmins?.[0]?.email

  return (
    <div className={classes.tabContent}>
      <BillingActionButtons
        invoice={invoice}
        editLocked={editLocked}
        {...invoiceHandlers}
        disabled={actionLoading}
        membershipInactive={membershipInactive}
      />

      <section className={classes.invoiceCard}>
        <div className={classes.invoiceHeader}>
          <h6 className={classNames(classes.sectionTitle, classes.cardTitle)}>
            {t('enrollmentsBilling.drawer.invoiceNumber', { number: invoice.invoiceNumber })}
          </h6>
          <BillingStatusBadge display={invoiceDisplay} />
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.program')}</span>
          <span className={classes.detailValue}>{course?.name}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.facility')}</span>
          <span className={classes.detailValue}>{course?.communityName || '-'}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.coach')}</span>
          <span className={classes.detailValue}>{course?.coachName || '-'}</span>
        </div>

        {familyAdminEmail && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.familyAdminEmail')}</span>
            <span className={classes.detailValue}>{familyAdminEmail}</span>
          </div>
        )}

        {course?.startDate && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.programDates')}</span>
            <span className={classes.detailValue}>
              {moment(course.startDate).format('MM/DD/YYYY')}
              {course.endDate && `-${moment(course.endDate).format('MM/DD/YYYY')}`}
            </span>
          </div>
        )}

        {invoice.paymentMethod && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.paymentMethod')}</span>
            <span
              className={classNames(classes.detailValue, classes.paymentMethodValue, {
                [classes.paymentMethodValuePaid]: invoice.status === 'paid',
              })}
            >
              {formatPaymentMethodDisplay(invoice, t)}
            </span>
          </div>
        )}

        {lineItems.length > 0 && (
          <div className={classes.lineItemsSection}>
            {lineItems.map(item => (
              <div key={item.id} className={classes.lineItemRow}>
                <span className={classes.lineItemLabel}>
                  {item.lineType === 'discount' ? t('enrollmentsBilling.drawer.discount') : item.description}
                </span>
                <span className={classes.lineItemAmount}>
                  {currency ? displayWithCurrency(Math.abs(parseFloat(item.amount)), currency) : `$${Math.abs(parseFloat(item.amount)).toFixed(2)}`}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className={classes.totalRow}>
          <span className={classes.totalLabel}>{t('enrollmentsBilling.drawer.total')}</span>
          <span className={classes.totalAmount}>
            {currency ? displayWithCurrency(invoice.total, currency) : `$${parseFloat(invoice.total).toFixed(2)}`}
          </span>
        </div>

        {invoice.notes && (
          <div className={classes.notesSection}>
            <span className={classes.notesTitle}>{t('enrollmentsBilling.drawer.additionalNotes')}</span>
            <p className={classes.notesText}>{invoice.notes}</p>
          </div>
        )}
      </section>
    </div>
  )
}

BillingTab.propTypes = {
  enrollment: PropTypes.object.isRequired,
  invoiceHandlers: PropTypes.object,
  onMarkPaymentStatus: PropTypes.func,
  actionLoading: PropTypes.bool,
  billingEnabled: PropTypes.bool,
  membershipInactive: PropTypes.bool,
}

// --- Billing Action Buttons ---

const BillingActionButtons = ({
  invoice, editLocked, onSendReminder, onMarkPaid, onEdit, onRefund, onMarkOutstanding, disabled, membershipInactive,
}) => {
  const { t } = useTranslation()
  if (!invoice) { return null }

  const isOutstanding = invoice.status === 'outstanding'
  const isFullyRefunded = invoice.status === 'refunded'
  const isPaid = invoice.status === 'paid'

  if (isOutstanding) {
    return (
      <div className={classes.actionButtons}>
        <button className={classes.actionButton} onClick={onSendReminder} disabled={disabled}>
          <MailIcon className={classes.billingActionIcon} /> {t('enrollmentsBilling.drawer.sendReminder')}
        </button>
        <button className={classNames(classes.actionButton, classes.pushRight)} onClick={onMarkPaid} disabled={disabled}>
          <span className={classes.markPaidCheckbox}><CheckmarkIcon className={classes.markPaidCheckmark} /></span>{' '}
          {t('enrollmentsBilling.drawer.markAsPaid')}
        </button>
        {!editLocked && (
          <button className={classes.actionButton} onClick={onEdit} disabled={disabled}>
            <EditIcon className={classes.billingActionIcon} /> {t('enrollmentsBilling.drawer.edit')}
          </button>
        )}
      </div>
    )
  }

  return (
    <>
      <div className={classes.actionButtons}>
        {!isFullyRefunded && (
          <button className={classes.actionButton} onClick={onRefund} disabled={disabled || membershipInactive}>
            <RefundIcon className={classes.actionIcon} /> {t('enrollmentsBilling.drawer.refund')}
          </button>
        )}
        {/* A refunded/partially-refunded invoice can't be reverted to outstanding, and neither can an
            online payment (Stripe captured the money) or a dropped enrollment. */}
        {isPaid && !editLocked && (
          <button className={classes.actionButton} onClick={onMarkOutstanding} disabled={disabled}>
            {t('enrollmentsBilling.drawer.markAsOutstanding')}
          </button>
        )}
      </div>
      {membershipInactive && !isFullyRefunded && <InactiveMemberNotice />}
    </>
  )
}

BillingActionButtons.propTypes = {
  invoice: PropTypes.object,
  editLocked: PropTypes.bool,
  onSendReminder: PropTypes.func,
  onMarkPaid: PropTypes.func,
  onEdit: PropTypes.func,
  onRefund: PropTypes.func,
  onMarkOutstanding: PropTypes.func,
  disabled: PropTypes.bool,
  membershipInactive: PropTypes.bool,
}

// --- Main component PropTypes ---

EnrollmentDetailDrawer.propTypes = {
  enrollment: PropTypes.object.isRequired,
  communityId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onActionComplete: PropTypes.func,
}

export default EnrollmentDetailDrawer
